import { FC, useEffect } from 'react';

import useDC from '@eneco/dc/useDC';
import { Header, Layout, NextButton, Overview } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { Grid, Stack, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import {
  DC_Domain_Models_Customers_Gender,
  DC_Customers_Client_Models_CustomerDiscontinueIntakeCustomerSecureResponseModel,
} from '@monorepo-types/dc';
import { usePlaceholderContent } from '@sitecore/common';
import { DeceasedFlowOverviewStepRendering } from '@sitecore/types/DeceasedFlowOverviewStep';
import { useMachine } from '@xstate/react';
import { useForm } from 'react-hook-form';

import { FlowContextDeceased } from '../../context';
import { summaryMachine } from '../machines/summaryMachine';

const formatDateToISO = (date: string | undefined): string | undefined => {
  if (!date) return undefined;
  const [day, month, year] = date.split('-');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

const decorateDataForDC = (data: DecorateDataForDCArgs) => {
  const transformedData = {
    customer: {
      postalCode: data.deceasedCustomer?.address?.postalCode,
      houseNumber: data.deceasedCustomer!.address!.houseNumber!,
      houseNumberSuffix: data.deceasedCustomer?.address?.houseNumberSuffix,
      dateDeceased: formatDateToISO(data.dateDeceased)!,
      keyDateNewAddress: formatDateToISO(data.keyDateNewAddress),
    },
    contactPerson: {
      initials: data.contactPerson?.initials ?? null,
      surnamePreposition: data.contactPerson?.surnamePreposition ?? null,
      surname: data.contactPerson?.surname ?? null,
      gender: data.contactPerson!.gender,
      street: data.deceasedCustomer?.address?.street ?? null,
      city: data.deceasedCustomer?.address?.city ?? null,
      postalCode: data.contactPerson?.postalCode ?? null,
      houseNumber: data.contactPerson!.houseNumber! ?? null,
      houseNumberSuffix: data.contactPerson?.houseNumberSuffix ?? null,
      phoneNumber: data.contactPerson?.phoneNumber ?? null,
      emailAddress: data.contactPerson?.emailAddress ?? null,
      changePaymentMethod: data!.changePaymentMethod! ?? null,
    },
  };
  return transformedData;
};

const WIJZIG = 'wijzig';

interface DecorateDataForDCArgs {
  deceasedCustomer?: DC_Customers_Client_Models_CustomerDiscontinueIntakeCustomerSecureResponseModel;
  dateDeceased: string | undefined;
  keyDateNewAddress: string | undefined;
  contactPerson:
    | {
        initials: string;
        surnamePreposition?: string | null;
        surname: string;
        gender: DC_Domain_Models_Customers_Gender;
        street?: string | null;
        city?: string | null;
        postalCode: string;
        houseNumber?: number | null;
        houseNumberSuffix?: string | null;
        phoneNumber: string;
        emailAddress: string;
      }
    | undefined;
  changePaymentMethod: boolean | undefined;
}

export const Summary: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextDeceased>();
  const { send: sendToFlowMachine } = useFlowActorRef();

  const deceasedCustomer = useFlowSelector(state => state.context.deceasedCustomer);
  const dateDeceased = useFlowSelector(state => state.context.dateDeceased);
  const keyDateNewAddress = useFlowSelector(state => state.context.keyDateNewAddress);
  const contactPerson = useFlowSelector(state => state.context.contactPerson);
  const changePaymentMethod = useFlowSelector(state => state.context.changePaymentMethod);
  const customerId = useFlowSelector(state => state.context.customerId);
  const { label, businessUnit } = useDC();

  const { trackFunnelCompleted } = useTracking();

  const { DeceasedFlowOverviewStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeceasedFlowOverviewStep: DeceasedFlowOverviewStepRendering;
  }>();

  const {
    handleSubmit,
    formState: { isSubmitSuccessful },
  } = useForm({
    mode: 'onSubmit',
  });

  const [state, sendToSummaryMachine] = useMachine(summaryMachine);

  const isSubmitting = state.matches('SUBMITTING');

  useEffect(() => {
    if (state.matches('SUCCESS')) {
      trackFunnelCompleted({ funnel: 'DECEASE_FLOW_CANCELATION' });
    }
  }, [sendToFlowMachine, state, trackFunnelCompleted]);

  useEffect(() => {
    if (state.matches('SUCCESS')) {
      sendToFlowMachine({ type: 'NEXT_PAGE' });
    }
  }, [sendToFlowMachine, state]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      decorateDataForDC({
        deceasedCustomer,
        dateDeceased,
        keyDateNewAddress,
        contactPerson,
        changePaymentMethod,
      });
      sendToSummaryMachine({
        type: 'SUBMIT',
        config: { businessUnit, label },
        values: {
          customerId,
          deceaesedInformation: decorateDataForDC({
            deceasedCustomer,
            dateDeceased,
            keyDateNewAddress,
            contactPerson,
            changePaymentMethod,
          }),
        },
      });
    }
  }, [
    businessUnit,
    changePaymentMethod,
    contactPerson,
    customerId,
    dateDeceased,
    deceasedCustomer,
    isSubmitSuccessful,
    keyDateNewAddress,
    label,
    sendToSummaryMachine,
  ]);

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>
      <Overview>
        <Overview.Header>{textualData?.contractCard?.title?.value}</Overview.Header>
        <Overview.Details type="profile" title={textualData?.contractCard?.desiredActionLabel?.value ?? ''}>
          <Text as="p">{textualData?.contractCard?.actionName?.value ?? ''}</Text>
        </Overview.Details>
      </Overview>

      <Overview>
        <Overview.Header>{textualData?.deceasedPersonDetailsCard?.title?.value}</Overview.Header>
        <Overview.Details
          editButton={{
            label: WIJZIG,
            onClick: () => {
              sendToFlowMachine({
                type: 'GOTO',
                stepName: 'STEP_DECEASED_DETAILS',
              });
            },
          }}
          type="profile"
          title={textualData?.deceasedPersonDetailsCard?.customerNumberLabel?.value ?? ''}>
          <Text as="p"> {customerId ?? '-'}</Text>
        </Overview.Details>
      </Overview>

      <Overview>
        <Overview.Header>{textualData?.dateOfPassingCard?.title?.value}</Overview.Header>
        <Overview.Details
          editButton={{
            label: WIJZIG,
            onClick: () => {
              sendToFlowMachine({
                type: 'GOTO',
                stepName: 'STEP_DECEASE_DATE',
              });
            },
          }}
          type="profile"
          title={textualData?.dateOfPassingCard?.dateOfPassingLabel?.value ?? ''}>
          <Grid>
            <Text as="p">
              {textualData?.dateOfPassingCard?.givenDateLabel?.value ?? ''}: {dateDeceased}
            </Text>
          </Grid>
        </Overview.Details>
      </Overview>

      <Overview>
        <Overview.Header>{textualData?.keyHandoverDateCard?.title?.value}</Overview.Header>
        <Overview.Details
          editButton={{
            label: WIJZIG,
            onClick: () => {
              sendToFlowMachine({
                type: 'GOTO',
                stepName: 'STEP_KEY_HANDOVER',
              });
            },
          }}
          type="profile"
          title={textualData?.keyHandoverDateCard?.keyHandoverDateLabel?.value ?? ''}>
          <Text as="p">
            {textualData?.keyHandoverDateCard?.givenDateLabel?.value ?? ''}: {keyDateNewAddress ?? '-'}
          </Text>
        </Overview.Details>
      </Overview>

      <Overview>
        <Overview.Header>{textualData?.nextOfKinDetailsCard?.title?.value}</Overview.Header>
        <Overview.Details
          editButton={{
            label: WIJZIG,
            onClick: () => {
              sendToFlowMachine({
                type: 'GOTO',
                stepName: 'STEP_CONTACT_DETAILS',
              });
            },
          }}
          type="profile"
          title={textualData?.nextOfKinDetailsCard?.personalDetailsLabel?.value ?? ''}>
          <Text as="p">
            {textualData?.nextOfKinDetailsCard?.fullNameLabel?.value ?? ''}: {contactPerson?.initials}{' '}
            {contactPerson?.surnamePreposition} {contactPerson?.surname}
          </Text>
          <Text as="p">
            {textualData?.nextOfKinDetailsCard?.phoneNumberLabel?.value ?? ''}: {contactPerson?.phoneNumber}
          </Text>
          <Text as="p">
            {textualData?.nextOfKinDetailsCard?.emailAddressLabel?.value ?? ''}: {contactPerson?.emailAddress}
          </Text>
        </Overview.Details>
        <Overview.Details
          editButton={{
            label: WIJZIG,
            onClick: () => {
              sendToFlowMachine({
                type: 'GOTO',
                stepName: 'STEP_CONTACT_DETAILS',
              });
            },
          }}
          type="address"
          title={textualData?.nextOfKinDetailsCard?.addressLabel?.value ?? ''}>
          <Text as="p">
            {contactPerson?.street ?? ''} {contactPerson?.houseNumber ?? ''} {contactPerson?.houseNumberSuffix ?? ''}
          </Text>
          <Text as="p">
            {contactPerson?.postalCode ?? ''} {contactPerson?.city ?? ''}
          </Text>
        </Overview.Details>
      </Overview>

      <Overview>
        <Overview.Header>{textualData?.paymentDetailsCard?.title?.value}</Overview.Header>
        <Overview.Details
          editButton={{
            label: WIJZIG,
            onClick: () => {
              sendToFlowMachine({
                type: 'GOTO',
                stepName: 'STEP_INSTALLMENT',
              });
            },
          }}
          type="profile"
          title={textualData?.paymentDetailsCard?.paymentMethodChangedLabel?.value ?? ''}>
          <Text as="p">
            {textualData?.paymentDetailsCard?.paymentMethodChangedLabel?.value ?? ''}:{' '}
            {changePaymentMethod === true ? 'ja' : 'nee'}
          </Text>
        </Overview.Details>
      </Overview>

      <Stack.Item>
        <Text>{textualData?.content?.nextStepText?.value}</Text>
      </Stack.Item>

      <Stack.Item>
        <NextButton isLoading={isSubmitting} alignX="start">
          {textualData?.content?.disclaimerText?.value}
        </NextButton>
      </Stack.Item>
    </Layout.Content>
  );
};
